import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { backTarget, useNavigationOriginVersion, type BackLinkState } from '@/lib/navigation-origin';
import { cn } from '@/lib/utils';

/**
 * The back half of the depth grammar (design grammar §4.2, §4.6).
 *
 * BACK RETURNS TO WHERE YOU CAME FROM (wave w1024, 2026-10-24, reversing the
 * 2026-08-29 fixed-parent rule). A family record has eight entry points —
 * Families, Users, Requests, Awards, claims, spend, polls, Threads —
 * and "← Families" from all of them sent a reader who came from
 * Requests to a list they never saw, without the filters they had set.
 *
 * Resolution (`backTarget` in `lib/navigation-origin.ts`):
 *   1. the in-app origin of this history entry, when the reader's role can
 *      open it — labelled with its nav name ("← Requests"), or "← Back"
 *      when the origin is itself a record page — with its query, so the
 *      filters return, and its scroll restored by `MainLayout`;
 *   2. otherwise (a deep link, a push, a fresh tab) the canonical parent from
 *      `parentFor`, role-aware, carrying the query that list was last left
 *      with.
 *
 * What did NOT change:
 *
 *   - **It is a `<Link>`, never `navigate(-1)`** (§4.6, §6 guard d). History
 *     can leave the app — a deep link's previous entry is another site, a
 *     fresh tab has none — and a real `to=` gives the control an address:
 *     hover shows it, middle-click opens it.
 *   - **It NAMES its destination,** in the nav module's own words (§4.4), so
 *     a sidebar rename moves every back link in the same commit.
 *   - **It renders NOTHING when there is no canonical parent** — on a sidebar
 *     destination, in the applicant shell, outside every shell — origin or
 *     not. Depth 0 never grows a trail; there are no breadcrumbs.
 *
 * The label is desktop grace IN THE HEADER: below `sm` that strip is already
 * crowded (the Material 3 / iOS HIG app-bar note in `Header.tsx`), so the
 * glyph stands alone there. A page-level back row has no such neighbour and
 * passes `label="always"`. The accessible name — "Back to Families" — carries
 * the destination at every width and contains the visible word, so
 * label-in-name (WCAG 2.5.3) holds whenever the label is showing.
 */
export interface BackLinkProps {
  /**
   * Resolve the canonical parent (the fallback, and whether a back link shows
   * at all) against this path instead of the current location — the header
   * passes `/chat` on a thread. The in-app origin is still the current
   * entry's, and is ignored when it IS this path.
   */
  pathname?: string;
  className?: string;
  /**
   * The reader's active role (`user.role`). It decides the routes two shells
   * share (`ROLE_PARENTS`, #1456), whether an origin is a page this hat can
   * open, and the fallback when the canonical parent is not (`ROLE_REACH` →
   * `homeFor(role)`). The header passes it. Omitted, the origin is used
   * without a role check (it never crosses shells anyway) and the fallback is
   * the role-independent canonical parent.
   */
  role?: string;
  /**
   * When the label shows beside the arrow.
   *
   * `responsive` (default) hides it below `sm`: that is the HEADER strip's
   * constraint — six adjacent controls already crowd it at 393dp (the
   * Material 3 / iOS HIG app-bar note in `Header.tsx`), and the accessible
   * name carries the destination at every width regardless.
   *
   * `always` is for a PAGE-level back row, which owns its own line and has
   * the room; §4.2's "it NAMES its destination" is worth more there than the
   * few pixels. `never` is icon-only.
   */
  label?: 'responsive' | 'always' | 'never';
}

export function BackLink({ pathname, className, role, label = 'responsive' }: BackLinkProps) {
  const location = useLocation();
  // Re-render once MainLayout's tracker records this entry's origin (it runs
  // in a layout effect, after this component's first render for the entry).
  useNavigationOriginVersion();
  // Header chrome on every shell page (#2109); the label is the nav row's own key.
  const { t } = useTranslation(['shell', 'common']);
  const target = backTarget({
    key: location.key,
    pathname: location.pathname,
    resolvePath: pathname,
    role,
  });

  if (!target) return null;

  const word = target.label ?? t('common:actions.back');
  const state: BackLinkState | undefined = target.backFrom ? { trfBackFrom: target.backFrom } : undefined;

  return (
    <Link
      to={target.to}
      state={state}
      aria-label={target.label ? t('header.backTo', { label: target.label }) : word}
      className={cn(
        // #2603 (D-2420): icon-only it is 40 px wide (20 px arrow + 10 px
        // padding each side) and the header squeeze could narrow it further;
        // on a coarse pointer the pill is at least 44 px and never shrinks.
        'inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-full px-2.5 text-body font-medium [@media(pointer:coarse)]:min-w-11',
        'text-stevens-ink-2 transition-colors hover:bg-stevens-bg-2 hover:text-stevens-ink',
        className,
      )}
    >
      <ArrowLeft className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
      {label !== 'never' && (
        <span className={label === 'always' ? undefined : 'hidden sm:inline'}>{word}</span>
      )}
    </Link>
  );
}
